// Full-screen save-progress overlay — blurred backdrop, pulsing icon card,
// animated progress bar that crawls to 90% while saving and snaps to 100%
// with a green check on success. Callers enforce a minimum display time so
// fast saves don't flicker.

import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Check } from 'lucide-react';

export type SavePhase = 'processing' | 'complete';

interface Props {
  isVisible: boolean;
  phase: SavePhase;
  title?: string;
  subtitle?: string;
  completeTitle?: string;
  completeSubtitle?: string;
}

export const SaveProgressOverlay: React.FC<Props> = ({
  isVisible,
  phase,
  title = 'Saving…',
  subtitle = 'Please wait…',
  completeTitle = 'Saved!',
  completeSubtitle = 'Done.',
}) => {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    if (!isVisible) { setProgress(0); return; }
    if (phase === 'complete') { setProgress(100); return; }
    setProgress(8);
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) { clearInterval(interval); return 90; }
        return prev + Math.random() * 12;
      });
    }, 120);
    return () => clearInterval(interval);
  }, [isVisible, phase]);

  return createPortal(
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[200] flex items-center justify-center bg-bg/80 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="bg-surface rounded-xl shadow-elevated border border-border p-8 max-w-sm w-full mx-4 text-center"
          >
            <div className="flex justify-center mb-5">
              {phase === 'complete' ? (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                  className="relative bg-success-soft rounded-full p-4"
                >
                  <Check className="h-8 w-8 text-success" strokeWidth={3} />
                </motion.div>
              ) : (
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
                  <div className="relative bg-primary-soft rounded-full p-4">
                    <Loader2 className="h-8 w-8 text-primary animate-spin" />
                  </div>
                </div>
              )}
            </div>

            <h3 className="text-lg font-semibold text-text-primary mb-2">
              {phase === 'complete' ? completeTitle : title}
            </h3>
            <p className="text-sm text-text-muted mb-5">
              {phase === 'complete' ? completeSubtitle : subtitle}
            </p>

            <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-alt">
              <div className="h-full rounded-full bg-primary transition-all duration-200" style={{ width: `${progress}%` }} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default SaveProgressOverlay;
